import { LoadingState } from '@lcs/shared/api';
import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, on } from '@ngrx/store';
import { CallDirectionTypes, OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import {
   presenceCallDetailsAdded,
   presenceCallDetailsUpdated,
} from 'src/app/signalr/call-details-hub/call-details-hub.actions';
import { extensionPhoneDndChanged } from 'src/app/signalr/extension-phone-hub/extension-phone-hub.actions';
import { CallDirectionTypesConverter } from '../../active-calls/state/register-reducer.factory';
import { PresenceContactCallDetail } from '../models/presence-contact-call-detail-model';
import { PresenceContactFilterOptions } from '../models/presence-contact-filter-option';
import { PresenceContactRegisterOptions } from '../models/presence-contact-register-options';
import { PresenceContactRegisterListItem } from '../models/presence-contact-register-results.model';
import * as presenceContactRegisterActions from './presence-contact-register.actions';
import { presenceContactRegisterAdapterActions } from './presence-contact-register.actions';

export const presenceContactRegisterFeatureKey = 'presenceContactRegister';
export interface PresenceContactRegisterState
   extends RegisterState<PresenceContactRegisterListItem, PresenceContactRegisterOptions, OrderingOption> {
   filters: PresenceContactFilterOptions[];
   presenceRefresh: boolean;
   registerItems: any;
}

const adapter: RegisterAdapter<PresenceContactRegisterListItem, PresenceContactRegisterOptions, OrderingOption> =
   createRegisterAdapter<PresenceContactRegisterListItem, PresenceContactRegisterOptions, OrderingOption>({
      selectId: (presenceContactRegisterItem) => {
         return presenceContactRegisterItem.extensionPhoneId ?? 0;
      },
   });

const entityAdapter: EntityAdapter<PresenceContactRegisterListItem> =
   createEntityAdapter<PresenceContactRegisterListItem>({
      selectId: (presenceContactRegisterItem) => {
         return presenceContactRegisterItem.extensionPhoneId ?? 0;
      },
   });

const initialState: PresenceContactRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   registerItems: [],
   presenceRefresh: false,
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'extension.name' }],
};

export const presenceContactRegisterReducer = adapter.createReducer(
   presenceContactRegisterActions.presenceContactRegisterAdapterActions,
   initialState,
   on(presenceContactRegisterActions.presenceContactRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(presenceContactRegisterActions.presenceContactRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         presenceContact: action.items,
         totalCount: action.totalCount,
         registerItems: action.items,
         presenceRefresh: true,
      };
   }),
   on(presenceContactRegisterActions.presenceContactRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(presenceContactRegisterActions.presenceContactRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         filters: action.filters,
      };
   }),
   on(presenceContactRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(extensionPhoneDndChanged, (state, action) => {
      return entityAdapter.updateOne(
         {
            id: action.extensionPhoneId,
            changes: { dndStatus: action.dndStatus },
         },
         state
      );
   }),
   on(presenceContactRegisterActions.editPresenceContactSubmit, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(presenceContactRegisterActions.editPresenceContactSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(presenceCallDetailsAdded, presenceCallDetailsUpdated, (state, action) => {
      const callDetail: PresenceContactCallDetail = convertActionToActiveRegisterListItem(action);
      let ids: { id: number; onCall: boolean }[] = [];
      if (callDetail.sourceExtension?.extensionPhone?.extensionPhoneId) {
         ids.push({
            id: callDetail.sourceExtension.extensionPhone.extensionPhoneId,
            onCall: callDetail.sourceExtension.extensionPhone.callDetailId ? true : false,
         });
      }
      if (callDetail.destinationExtension?.extensionPhone?.extensionPhoneId) {
         ids.push({
            id: callDetail.destinationExtension.extensionPhone.extensionPhoneId,
            onCall: callDetail.destinationExtension.extensionPhone.callDetailId ? true : false,
         });
      }
      callDetail.callSegmentsConnection?.items.forEach((callSegment) => {
         if (callSegment.extension?.extensionPhone?.extensionPhoneId) {
            ids.push({
               id: callSegment.extension.extensionPhone.extensionPhoneId,
               onCall: callSegment.extension.extensionPhone.callDetailId ? true : false,
            });
         }
      });
      const updates = ids.map((updateId) => ({
         id: updateId.id,
         changes: { onCall: updateId.onCall, callDetail: callDetail },
      }));

      return entityAdapter.updateMany(updates, {
         ...state,
      });
   })
);

export const presenceContactRegisterSelector = createFeatureSelector<PresenceContactRegisterState>(
   presenceContactRegisterFeatureKey
);
export const selectors = adapter.getSelectors(presenceContactRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(presenceContactRegisterSelector);

function convertActionToActiveRegisterListItem(action: { callDetail: any }): PresenceContactCallDetail {
   const callDetail: PresenceContactCallDetail = {
      callDetailId: action.callDetail?.callDetailId,
      callDestination: getCallExtension(
         { ...action.callDetail?.destinationExtension },
         action.callDetail?.callDirectionTypeId,
         action.callDetail?.destinationNumber ?? null,
         true
      ),
      callDirection: action.callDetail?.callDirectionTypeId
         ? CallDirectionTypes[CallDirectionTypesConverter[action.callDetail.callDirectionTypeId]]
         : null,
      callSegmentsConnection: action.callDetail?.callSegments
         ? {
              items: action.callDetail.callSegments,
           }
         : null,
      callSource: getCallExtension(
         { ...action.callDetail?.sourceExtension },
         action.callDetail.callDirectionTypeId,
         action.callDetail?.sourceNumber ?? null
      ),
      destinationExtension: action.callDetail?.destinationExtension ?? null,
      destinationNumber: action.callDetail?.destinationNumber ?? null,
      phoneNumber: action.callDetail?.phoneNumber?.phoneNumber ?? null,
      serverName: action.callDetail?.serverName ?? null,
      sipCallId: action.callDetail?.sipCallId ?? null,
      startTime: action.callDetail?.startTime ?? null,
      sourceExtension: action.callDetail?.sourceExtension ?? null,
   };
   return callDetail;
}
